// src/components/LandingPage.js
import { ArrowRight, CheckCircle, MessageSquare, Phone, PlayCircle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useConfig } from '../hooks/useConfig';
import Layout from './Layout';

const LandingPage = () => {
  const { site } = useConfig();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left">
              <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
                {site.company.tagline}
              </h1>
              <p className="mt-3 text-base text-gray-600 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl">
                {site.company.description}. Perfect for salons, barbershops, spas, and beauty businesses.
              </p>
              <div className="mt-8 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/register" className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 flex items-center justify-center">
                    Start {site.pricing.trialDays}-Day Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link to="/demo" className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-lg text-lg font-medium hover:border-gray-400 flex items-center justify-center">
                    <PlayCircle className="mr-2 h-5 w-5" />
                    Watch Demo
                  </Link>
                </div>
                <p className="mt-3 text-sm text-gray-500">
                  No setup fees • No contracts • Cancel anytime
                </p>
              </div>
            </div>
            <div className="mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-6 lg:flex lg:items-center">
              <div className="relative mx-auto w-full rounded-lg shadow-lg lg:max-w-md">
                <div className="bg-white rounded-lg p-6 shadow-xl">
                  <div className="flex items-center mb-4">
                    <MessageSquare className="h-6 w-6 text-green-500" />
                    <span className="ml-2 font-medium">WhatsApp Assistant</span>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-gray-100 rounded-lg p-3">
                      <p className="text-sm">👋 Hi! I'd like to book a haircut for tomorrow at 2pm</p>
                    </div>
                    <div className="bg-blue-100 rounded-lg p-3 ml-6">
                      <p className="text-sm">Perfect! I have 2:00 PM available with Sarah. Shall I book that for you?</p>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-3">
                      <p className="text-sm">Yes please!</p>
                    </div>
                    <div className="bg-blue-100 rounded-lg p-3 ml-6">
                      <p className="text-sm">✅ Booked! Your appointment is confirmed for tomorrow at 2:00 PM. See you then!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Tired of losing money to missed bookings?
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              The average salon loses $2,400+ monthly from missed calls and poor booking management
            </p>
          </div>
          
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {site.features.mainBenefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">{benefit.title}</h3>
                <p className="mt-2 text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Get started in minutes, not hours
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Our AI assistant integrates seamlessly with your existing workflow
            </p>
          </div>
          
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="mt-4 text-xl font-medium text-gray-900">Connect Your Phone</h3>
              <p className="mt-2 text-gray-600">Link your business WhatsApp and phone number in under 2 minutes</p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="mt-4 text-xl font-medium text-gray-900">Set Your Services</h3>
              <p className="mt-2 text-gray-600">Configure your services, pricing, and availability with our simple setup wizard</p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="mt-4 text-xl font-medium text-gray-900">Start Booking</h3>
              <p className="mt-2 text-gray-600">Your AI assistant immediately starts handling bookings, 24/7</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
                Works on WhatsApp & Phone
              </h2>
              <p className="mt-3 text-lg text-gray-600">
                Your customers can book however they prefer - through WhatsApp messages or traditional phone calls. Our AI understands both perfectly.
              </p>
              
              <div className="mt-8 space-y-4">
                {site.features.keyFeatures.slice(0, 3).map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="flex-shrink-0 h-6 w-6 text-green-500 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{feature}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mt-8 lg:mt-0">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg p-8 text-white">
                <h3 className="text-2xl font-bold">Perfect for:</h3>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {site.industries.map((industry) => (
                    <div key={industry.id}>{industry.icon} {industry.name}</div>
                  ))}
                </div>
                <Link to="/register" className="mt-6 bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 inline-block">
                  Start Your Free Trial
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Start with our {site.pricing.trialDays}-day free trial. No setup fees, no contracts.
            </p>
          </div>
          
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {site.pricing.plans.map((plan) => (
              <div key={plan.id} className={`bg-white rounded-lg shadow p-6 ${plan.popular ? 'ring-2 ring-blue-500 relative' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">Most Popular</span>
                  </div>
                )}
                <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                <div className="mt-4">
                  <span className="text-lg font-bold text-blue-600">Reach out for pricing</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {plan.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="ml-3 text-gray-600">{feature.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/pricing" className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700">
              View All Plans
            </Link>
            <p className="mt-2 text-sm text-gray-600">Try any plan free for {site.pricing.trialDays} days</p>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Built for businesses like yours
            </h2>
          </div>
          
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {site.testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.quote}"</p>
                <div className="font-medium text-gray-900">{testimonial.author}</div>
                <div className="text-sm text-gray-600">{testimonial.business}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Ready to transform your booking process?
          </h2>
          <p className="mt-4 text-xl text-blue-100">
            Be among the first to put AI to work for your bookings
          </p>
          <div className="mt-8">
            <Link to="/register" className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100">
              Start Your {site.pricing.trialDays}-Day Free Trial
            </Link>
            <p className="mt-4 text-blue-100">
              No credit card required • Setup in under 5 minutes
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LandingPage;